import { useState, useEffect, useCallback, useContext } from 'react';
import { supabase } from '@/lib/supabase';
import { AuthContext } from '@/lib/AuthContext';
import { logger } from '@/lib/logger';
import MaintenancePage from '@/pages/MaintenancePage';

/**
 * MaintenanceGuard component
 * Wraps the app and shows maintenance page when maintenance is active.
 * Super admins bypass the maintenance check.
 */
export default function MaintenanceGuard({ children }) {
  // Use useContext directly instead of useAuth hook to avoid throwing error
  // if context is not available (shouldn't happen, but defensive programming)
  const authContext = useContext(AuthContext);
  const userProfile = authContext?.userProfile || null;
  
  const [maintenanceData, setMaintenanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkCount, setCheckCount] = useState(0);

  // Check if user is super admin (bypasses maintenance)
  const isSuperAdmin = userProfile?.primary_role === 'super_admin' ||
                       userProfile?.active_role === 'super_admin';

  // Check maintenance status
  const checkMaintenance = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('is_maintenance_active');

      if (error) {
        logger.error('Error checking maintenance status:', error);
        // On error, allow access (fail open)
        setMaintenanceData(null);
        return;
      }

      // The function returns an array with one row
      if (data && data.length > 0 && data[0].is_active) {
        setMaintenanceData({
          isActive: true,
          title: data[0].title,
          message: data[0].message,
          endTime: data[0].end_time
        });
      } else {
        setMaintenanceData(null);
      }
    } catch (err) {
      logger.error('Maintenance check failed:', err);
      setMaintenanceData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial check and periodic refresh
  useEffect(() => {
    checkMaintenance();

    // Check every 30 seconds while maintenance is active
    const interval = setInterval(() => {
      if (maintenanceData?.isActive) {
        checkMaintenance();
        setCheckCount(prev => prev + 1);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [checkMaintenance, maintenanceData?.isActive]);

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    setLoading(true);
    await checkMaintenance();
    setCheckCount(prev => prev + 1);
  }, [checkMaintenance]);

  // Don't block initial render while checking maintenance
  // Only show maintenance page once we've confirmed maintenance IS active
  // This prevents blank page during initial load

  // If maintenance is active and user is NOT super admin, show maintenance page
  if (!loading && maintenanceData?.isActive && !isSuperAdmin) {
    return (
      <MaintenancePage
        title={maintenanceData.title}
        message={maintenanceData.message}
        endTime={maintenanceData.endTime}
        onRefresh={handleRefresh}
      />
    );
  }

  // Otherwise, render children (normal app)
  return children;
}
